import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { formatMoneyCHF } from '../utils/priceUtils';
import {
  FaSearch, FaTimes, FaCheck, FaTrash, FaSpinner, FaBoxes, FaTag,
} from 'react-icons/fa';
import { searchProducts, searchCompanies, fetchProductsByCompany } from '../services/apiService';
import { bulkAttachToCampaign } from '../services/apiService';

/**
 * Add products to a campaign — search + multiselect UX, mirroring the
 * existing BulkDiscountModal so admins use the same flow they already
 * know.
 *
 * Submit flow:
 *   1. Click "Add to campaign" → dryRun call to preview conflicts
 *   2. No conflicts → commit immediately (policy='skip', a no-op for
 *      empty conflict list).
 *   3. Conflicts present → show preview pane with Skip / Replace / Cancel.
 */
const CampaignBulkAddModal = ({ campaign, onClose, onCommitted }) => {
  // ── Selection panel state ──────────────────────────────────────────
  const [searchTerm, setSearchTerm] = useState('');
  const [companyFilter, setCompanyFilter] = useState('');
  const [companyQuery, setCompanyQuery] = useState('');
  const [companies, setCompanies] = useState([]);
  const [showCompanyDropdown, setShowCompanyDropdown] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);

  // Map<pharmacode, { pharmacode, name, basePrice, company, currentDiscount }>
  const [selected, setSelected] = useState(new Map());

  // ── Rule form state ────────────────────────────────────────────────
  const [kind, setKind] = useState('percent');
  const [amount, setAmount] = useState('');
  const [endsAt, setEndsAt] = useState(
    campaign?.endsAt ? new Date(campaign.endsAt).toISOString().slice(0, 16) : ''
  );

  // ── Submission state ───────────────────────────────────────────────
  const [step, setStep] = useState('select'); // 'select' | 'preview' | 'submitting'
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState(null);

  // Company typeahead — only kicks in after 2 chars.
  useEffect(() => {
    if (!companyQuery.trim() || companyQuery.trim().length < 2) {
      setCompanies([]);
      return;
    }
    const timer = setTimeout(async () => {
      try {
        const r = await searchCompanies(companyQuery.trim(), 10);
        setCompanies(r?.data?.companies || r?.companies || []);
      } catch { setCompanies([]); }
    }, 200);
    return () => clearTimeout(timer);
  }, [companyQuery]);

  // Debounced product search.
  useEffect(() => {
    const term = searchTerm.trim();
    const hasFilters = term.length >= 2 || !!companyFilter;
    if (!hasFilters) { setSearchResults([]); return; }

    setIsSearching(true);
    const timer = setTimeout(async () => {
      try {
        const response = companyFilter
          ? await fetchProductsByCompany(companyFilter, 1, 50)
          : await searchProducts(term, 1, 50);
        setSearchResults(response?.data?.products || []);
      } catch { setSearchResults([]); }
      finally { setIsSearching(false); }
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm, companyFilter]);

  // Normalize search hits into row shape we render. Dedup by pharmacode
  // defensively — the search endpoint already collapses cross-hit
  // duplicates, but the HCI catalog can split one article across multiple
  // parent docs, so keeping a Map here makes the modal robust against any
  // future regression there. First occurrence wins (it's the most relevant
  // hit per ES scoring).
  const rows = useMemo(() => {
    const byCode = new Map();
    for (const p of searchResults) {
      const article = p.articles?.[0];
      if (!article || !article.pharmacode) continue;
      if (byCode.has(article.pharmacode)) continue;
      const name =
        article.description?.longDescription ||
        article.description?.description ||
        article.longDescription ||
        `Pharmacode ${article.pharmacode}`;
      const company = Array.isArray(p.companies) && p.companies[0]?.name
        ? p.companies[0].name : null;
      const basePrice =
        article.price?.basePrice ?? article.basePrice ?? article.publicPrice?.price ?? null;
      byCode.set(article.pharmacode, {
        pharmacode: article.pharmacode,
        name,
        company,
        basePrice,
        currentDiscount: article.discount || null,
      });
    }
    return Array.from(byCode.values());
  }, [searchResults]);

  const toggleSelect = useCallback((row) => {
    setSelected((prev) => {
      const next = new Map(prev);
      if (next.has(row.pharmacode)) next.delete(row.pharmacode);
      else next.set(row.pharmacode, row);
      return next;
    });
  }, []);

  const removeSelected = useCallback((code) => {
    setSelected((prev) => {
      const next = new Map(prev);
      next.delete(code);
      return next;
    });
  }, []);

  const selectAllVisible = useCallback(() => {
    setSelected((prev) => {
      const next = new Map(prev);
      for (const r of rows) if (!next.has(r.pharmacode)) next.set(r.pharmacode, r);
      return next;
    });
  }, [rows]);

  const deselectAllVisible = useCallback(() => {
    setSelected((prev) => {
      const next = new Map(prev);
      for (const r of rows) next.delete(r.pharmacode);
      return next;
    });
  }, [rows]);

  // ── Submit flow ────────────────────────────────────────────────────

  const buildBody = () => ({
    pharmacodes: Array.from(selected.keys()),
    kind,
    amount: Number(amount),
    endsAt: endsAt ? new Date(endsAt).toISOString() : null,
  });

  const validate = () => {
    if (selected.size === 0) return 'Select at least one product.';
    const amt = Number(amount);
    if (!Number.isFinite(amt) || amt <= 0) return 'Enter a valid discount amount.';
    if (kind === 'percent' && amt >= 100) return 'Percentage must be less than 100.';
    return null;
  };

  const onAddClick = async () => {
    const err = validate();
    if (err) { setError(err); return; }
    setError(null);
    setStep('submitting');
    try {
      const dry = await bulkAttachToCampaign(campaign._id, buildBody(), true);
      const data = dry.data || { ready: [], conflicts: [], errors: [] };
      setPreview(data);

      // Skip the preview screen entirely if there's nothing to decide:
      // commit straight away.
      if (data.conflicts.length === 0 && data.errors.length === 0) {
        await commit('skip');
        return;
      }
      setStep('preview');
    } catch (e) {
      setError(e.response?.data?.error || e.message || 'Preview failed.');
      setStep('select');
    }
  };

  const commit = async (policy) => {
    setStep('submitting');
    setError(null);
    try {
      await bulkAttachToCampaign(campaign._id, {
        ...buildBody(),
        conflictPolicy: policy,
      }, false);
      onCommitted();
    } catch (e) {
      setError(e.response?.data?.error || e.message || 'Commit failed.');
      setStep('preview');
    }
  };

  const fmt = (offer) => offer.kind === 'percent'
    ? `${offer.amount}% off`
    : `${formatMoneyCHF(offer.amount)} flat`;

  return (
    <AnimatePresence>
      <motion.div
        className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      >
        <motion.div
          className="w-full max-w-6xl max-h-[92vh] flex flex-col bg-white rounded-2xl shadow-2xl overflow-hidden"
          initial={{ opacity: 0, scale: 0.96, y: 16 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.96, y: 16 }}
          transition={{ duration: 0.18 }}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-5 border-b border-gray-200 bg-gradient-to-r from-red-50 to-pink-50">
            <div className="flex items-center gap-3">
              <div className="flex items-center justify-center w-11 h-11 rounded-xl bg-gradient-to-r from-red-500 to-pink-600 text-white shadow-md">
                <FaBoxes className="text-xl" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-gray-900">Add products to "{campaign.name}"</h2>
                <p className="text-sm text-gray-600">
                  Search, multi-select, and apply one discount rule to every chosen product.
                </p>
              </div>
            </div>
            <button onClick={onClose} className="p-2 rounded-lg hover:bg-white/60 text-gray-500" aria-label="Close">
              <FaTimes />
            </button>
          </div>

          {/* Body */}
          <div className="flex-1 overflow-hidden flex flex-col lg:flex-row">
            {/* Left: search + results */}
            <div className="flex-1 min-w-0 border-r border-gray-200 flex flex-col" style={{ display: step === 'preview' || step === 'submitting' ? 'none' : 'flex' }}>
              <div className="px-5 py-4 border-b border-gray-200 space-y-2">
                <div className="relative">
                  <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Search products by name, brand, pharmacode..."
                    className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-red-500"
                  />
                </div>

                <div className="relative">
                  <input
                    type="text"
                    value={companyFilter || companyQuery}
                    onChange={(e) => {
                      if (companyFilter) setCompanyFilter('');
                      setCompanyQuery(e.target.value);
                      setShowCompanyDropdown(true);
                    }}
                    onFocus={() => setShowCompanyDropdown(true)}
                    placeholder="Filter by company (optional)"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-red-500"
                  />
                  {companyFilter && (
                    <button
                      type="button"
                      onClick={() => { setCompanyFilter(''); setCompanyQuery(''); setCompanies([]); }}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      aria-label="Clear company filter"
                    ><FaTimes /></button>
                  )}
                  {showCompanyDropdown && companies.length > 0 && (
                    <ul className="absolute z-10 top-full left-0 right-0 mt-1 max-h-48 overflow-y-auto bg-white border border-gray-200 rounded-lg shadow-lg">
                      {companies.map((c, i) => (
                        <li
                          key={`${c}-${i}`}
                          className="px-3 py-2 text-sm hover:bg-gray-50 cursor-pointer"
                          onClick={() => {
                            setCompanyFilter(typeof c === 'string' ? c : c?.name || '');
                            setCompanyQuery('');
                            setShowCompanyDropdown(false);
                          }}
                        >{typeof c === 'string' ? c : c?.name}</li>
                      ))}
                    </ul>
                  )}
                </div>

                {rows.length > 0 && (
                  <div className="flex items-center justify-between text-xs text-gray-500">
                    <span>{rows.length} result{rows.length === 1 ? '' : 's'}</span>
                    <div className="flex gap-2">
                      <button type="button" onClick={selectAllVisible} className="text-red-600 hover:underline">Select all</button>
                      <span>·</span>
                      <button type="button" onClick={deselectAllVisible} className="text-gray-600 hover:underline">Deselect all</button>
                    </div>
                  </div>
                )}
              </div>

              <div className="flex-1 overflow-y-auto px-5 py-3">
                {isSearching ? (
                  <div className="flex items-center gap-2 text-gray-500 text-sm py-8 justify-center">
                    <FaSpinner className="animate-spin" /> Searching...
                  </div>
                ) : rows.length === 0 ? (
                  <div className="text-gray-400 text-sm py-8 text-center">
                    {searchTerm.length === 0 && !companyFilter
                      ? 'Type at least 2 characters or pick a company to start.'
                      : 'No matches.'}
                  </div>
                ) : (
                  <ul className="space-y-1">
                    {rows.map((r) => {
                      const isSelected = selected.has(r.pharmacode);
                      return (
                        <li
                          key={r.pharmacode}
                          onClick={() => toggleSelect(r)}
                          className={`flex items-center gap-3 px-3 py-2 rounded-lg border cursor-pointer transition-colors ${
                            isSelected
                              ? 'border-red-300 bg-red-50'
                              : 'border-gray-200 hover:bg-gray-50'
                          }`}
                        >
                          <span className={`flex items-center justify-center w-5 h-5 rounded border ${isSelected ? 'bg-red-500 border-red-500 text-white' : 'border-gray-300'}`}>
                            {isSelected && <FaCheck className="text-[10px]" />}
                          </span>
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-medium text-gray-900 truncate">{r.name}</div>
                            <div className="text-xs text-gray-500 truncate">
                              <span className="font-mono">{r.pharmacode}</span>
                              {r.company && <> · {r.company}</>}
                              {r.basePrice != null && <> · {formatMoneyCHF(r.basePrice)}</>}
                              {r.currentDiscount && (
                                <span className="ml-1 inline-flex items-center px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 text-[10px] font-semibold">
                                  already discounted
                                </span>
                              )}
                            </div>
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            </div>

            {/* Right: selected + rule + actions OR preview */}
            <div className="lg:w-[420px] flex flex-col">
              {step === 'preview' && preview && (
                <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3 text-sm">
                  <div className="p-3 bg-green-50 border border-green-200 rounded">
                    <div className="font-medium text-green-800">✅ Ready to add: {preview.ready.length}</div>
                  </div>
                  {preview.conflicts.length > 0 && (
                    <div className="p-3 bg-amber-50 border border-amber-200 rounded">
                      <div className="font-medium text-amber-800 mb-2">
                        ⚠️ Conflicts: {preview.conflicts.length} — these pharmacodes already have an active discount
                      </div>
                      <div className="max-h-48 overflow-y-auto">
                        <table className="w-full text-xs">
                          <thead className="text-gray-500 text-left sticky top-0 bg-amber-50">
                            <tr>
                              <th className="py-1 pr-2">Code</th>
                              <th className="py-1 pr-2">Existing</th>
                              <th className="py-1">New</th>
                            </tr>
                          </thead>
                          <tbody>
                            {preview.conflicts.map((c) => (
                              <tr key={c.pharmacode} className="border-t border-amber-200">
                                <td className="py-1 pr-2 font-mono">{c.pharmacode}</td>
                                <td className="py-1 pr-2">
                                  {fmt(c.existing)}
                                  {c.existing.label && <div className="text-gray-500">{c.existing.label}</div>}
                                </td>
                                <td className="py-1">{fmt(c.newOffer)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                  {preview.errors.length > 0 && (
                    <div className="p-3 bg-red-50 border border-red-200 rounded">
                      <div className="font-medium text-red-800 mb-2">❌ Errors: {preview.errors.length}</div>
                      <ul className="text-xs space-y-0.5 max-h-32 overflow-y-auto">
                        {preview.errors.map((e, i) => (
                          <li key={i} className="font-mono">{e.pharmacode}: {e.reason}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}

              {step === 'submitting' && (
                <div className="flex-1 flex items-center justify-center text-gray-500 text-sm gap-2">
                  <FaSpinner className="animate-spin" /> Saving...
                </div>
              )}

              {(step === 'select') && (
                <>
                  {/* Selected list */}
                  <div className="px-5 py-3 border-b border-gray-200">
                    <div className="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-2">
                      Selected ({selected.size})
                    </div>
                    {selected.size === 0 ? (
                      <div className="text-xs text-gray-400 py-4 text-center">No products selected yet.</div>
                    ) : (
                      <ul className="max-h-40 overflow-y-auto space-y-1">
                        {Array.from(selected.values()).map((r) => (
                          <li key={r.pharmacode} className="flex items-center gap-2 text-sm">
                            <span className="font-mono text-xs text-gray-500 w-16 flex-shrink-0">{r.pharmacode}</span>
                            <span className="flex-1 truncate">{r.name}</span>
                            <button
                              type="button"
                              onClick={() => removeSelected(r.pharmacode)}
                              className="text-gray-400 hover:text-red-600"
                              aria-label="Remove"
                            ><FaTrash className="text-xs" /></button>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>

                  {/* Rule form */}
                  <div className="px-5 py-4 space-y-3 flex-1 overflow-y-auto">
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <label className="block">
                        <span className="text-gray-700 text-xs">Kind</span>
                        <select
                          value={kind}
                          onChange={(e) => setKind(e.target.value)}
                          className="mt-1 w-full px-2 py-1.5 border border-gray-300 rounded text-sm"
                        >
                          <option value="percent">Percent</option>
                          <option value="fixed">Fixed CHF</option>
                        </select>
                      </label>
                      <label className="block">
                        <span className="text-gray-700 text-xs">Amount</span>
                        <input
                          type="number" min="0" step="0.01"
                          value={amount}
                          onChange={(e) => setAmount(e.target.value)}
                          className="mt-1 w-full px-2 py-1.5 border border-gray-300 rounded text-sm"
                        />
                      </label>
                    </div>
                    <label className="block text-sm">
                      <span className="text-gray-700 text-xs">Per-discount ends at (optional)</span>
                      <input
                        type="datetime-local"
                        value={endsAt}
                        onChange={(e) => setEndsAt(e.target.value)}
                        className="mt-1 w-full px-2 py-1.5 border border-gray-300 rounded text-sm"
                      />
                      <span className="block mt-1 text-[10px] text-gray-400">
                        Campaign envelope: {campaign.endsAt ? new Date(campaign.endsAt).toLocaleString() : 'no end date'}.
                        Discounts can end earlier than the campaign but not later.
                      </span>
                    </label>

                    {error && (
                      <div className="p-2 bg-red-50 border border-red-200 text-red-700 rounded text-xs">
                        {typeof error === 'string' ? error : JSON.stringify(error)}
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Footer */}
          <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-2 flex-wrap">
            {step === 'select' && (
              <>
                <button type="button" onClick={onClose} className="px-4 py-2 rounded-lg border border-gray-300 hover:bg-gray-50">Cancel</button>
                <button
                  type="button"
                  onClick={onAddClick}
                  disabled={selected.size === 0 || !amount}
                  className="px-4 py-2 rounded-lg bg-[#E1444B] text-white hover:opacity-90 disabled:opacity-50 inline-flex items-center gap-2"
                >
                  <FaTag /> Add {selected.size} product{selected.size === 1 ? '' : 's'}
                </button>
              </>
            )}
            {step === 'preview' && (
              <>
                <button type="button" onClick={() => setStep('select')} className="px-4 py-2 rounded-lg border border-gray-300 hover:bg-gray-50">Back</button>
                <button type="button" onClick={onClose} className="px-4 py-2 rounded-lg border border-gray-300 hover:bg-gray-50">Cancel</button>
                {preview && preview.conflicts.length === 0 ? (
                  <button
                    type="button"
                    onClick={() => commit('skip')}
                    disabled={preview.ready.length === 0}
                    className="px-4 py-2 rounded-lg bg-[#E1444B] text-white hover:opacity-90 disabled:opacity-50"
                  >Add {preview.ready.length}</button>
                ) : (
                  <>
                    <button
                      type="button"
                      onClick={() => commit('skip')}
                      disabled={!preview || preview.ready.length === 0}
                      className="px-4 py-2 rounded-lg bg-gray-700 text-white hover:opacity-90 disabled:opacity-50"
                    >Skip conflicts ({preview?.ready.length || 0})</button>
                    <button
                      type="button"
                      onClick={() => commit('replace')}
                      className="px-4 py-2 rounded-lg bg-[#E1444B] text-white hover:opacity-90"
                    >Replace all ({(preview?.ready.length || 0) + (preview?.conflicts.length || 0)})</button>
                  </>
                )}
              </>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default CampaignBulkAddModal;
